import { geoBounds, geoCentroid } from "d3-geo";
import { FC, useCallback, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

import { NUMERIC_TO_ISO2 } from "@/components/panels/countryEditor/constants";
import { Box, Card, Skeleton } from "@/components/ui";
import API from "@/hooks/api";
import type { CountryValue } from "@/types/resources/metadata";

const PURPLE = "#7c3aed";
const PURPLE_BORDER = "#a78bfa";
const FILL_DEFAULT = "#e5e7eb";
// react-simple-maps' ComposableMap defaults to an 800-wide viewBox; pairing it
// with MAP_HEIGHT yields the map's natural aspect ratio. We drive the container
// by aspect-ratio (not a fixed pixel height) so the map stays proportional on
// every screen — no wasted vertical bands on phones.
const MAP_HEIGHT = 300;
const MAP_ASPECT = 800 / MAP_HEIGHT;
const WORLD_GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

interface GeoFeature {
  [key: string]: unknown;
  id: string;
  properties: Record<string, string>;
  rsmKey: string;
  type: string;
  geometry: GeoJSON.Geometry;
}

const resolveProperty = (obj: GeoFeature, path: string): string | undefined => {
  const parts = path.split(".");
  let cur: GeoFeature | Record<string, unknown> = obj;
  for (const p of parts) {
    const next = (cur as Record<string, unknown>)[p];
    if (next == null || typeof next !== "object") {
      return typeof next === "string" ? next : undefined;
    }
    cur = next as Record<string, unknown>;
  }
  return undefined;
};

const PIN_RED = "#ea4335";

const MapPin: FC = () => (
  <g transform="translate(-12, -24)">
    <path
      d="M12 0C7.03 0 3 4.03 3 9c0 6.75 9 15 9 15s9-8.25 9-15c0-4.97-4.03-9-9-9z"
      fill={PIN_RED}
      stroke="#b31412"
      strokeWidth={0.5}
    />
    <circle cx={12} cy={9} fill="#7a1e1e" r={3.5} />
  </g>
);

const geoStyle = (highlighted: boolean) => ({
  cursor: "default" as const,
  fill: highlighted ? PURPLE : FILL_DEFAULT,
  outline: "none",
  stroke: highlighted ? PURPLE_BORDER : "#fff",
  strokeWidth: 0.75,
});

/* ── World map fallback (no state-level map available) ── */

const computeCountryZoom = (
  feature: GeoJSON.Feature
): { center: [number, number]; scale: number } => {
  const center = geoCentroid(feature) as [number, number];
  const [[x0, y0], [x1, y1]] = geoBounds(feature);
  const lngSpan = Math.abs(x1 - x0);
  const latSpan = Math.abs(y1 - y0);
  const maxSpan = Math.max(lngSpan, latSpan, 1);
  const scale = Math.min(18000 / maxSpan, 6000);
  return { center, scale: Math.max(scale, 400) };
};

const WorldFallbackMap: FC<{ countryCode: CountryValue }> = ({
  countryCode,
}) => {
  const [focus, setFocus] = useState<{
    center: [number, number];
    scale: number;
  } | null>(null);

  return (
    <Box
      style={{ aspectRatio: MAP_ASPECT, position: "relative", width: "100%" }}
    >
      {/* Hidden map to compute focus from geo data */}
      {!focus && (
        <>
          <Box style={{ position: "absolute", inset: 0, zIndex: 1 }}>
            <PersonaLocationMapSkeleton />
          </Box>
          <Box style={{ opacity: 0, position: "absolute" }}>
            <ComposableMap height={1} style={{ height: 1, width: 1 }}>
              <Geographies geography={WORLD_GEO_URL}>
                {({ geographies }: { geographies: GeoFeature[] }) => {
                  const match = geographies.find(
                    (g) => NUMERIC_TO_ISO2[g.id] === countryCode
                  );
                  const zoom = match
                    ? computeCountryZoom(match as unknown as GeoJSON.Feature)
                    : { center: [0, 20] as [number, number], scale: 140 };
                  setTimeout(() => setFocus(zoom), 0);
                  return null;
                }}
              </Geographies>
            </ComposableMap>
          </Box>
        </>
      )}

      {focus && (
        <Card className="absolute inset-0 p-0 rounded-2xl overflow-hidden border border-outline-200">
          <ComposableMap
            height={MAP_HEIGHT}
            projectionConfig={{ center: focus.center, scale: focus.scale }}
            style={{ display: "block", height: "100%", width: "100%" }}
          >
            <Geographies geography={WORLD_GEO_URL}>
              {({ geographies }: { geographies: GeoFeature[] }) =>
                geographies.map((geo) => {
                  const iso2 = NUMERIC_TO_ISO2[geo.id];
                  return (
                    <Geography
                      geography={geo}
                      key={geo.rsmKey}
                      style={{
                        default: geoStyle(iso2 === countryCode),
                        hover: geoStyle(iso2 === countryCode),
                        pressed: geoStyle(iso2 === countryCode),
                      }}
                    />
                  );
                })
              }
            </Geographies>

            <Marker coordinates={focus.center}>
              <MapPin />
            </Marker>
          </ComposableMap>
        </Card>
      )}
    </Box>
  );
};

/* ── State-level map (subnational geography available) ── */

interface PersonaLocationMapProps {
  countryCode: CountryValue;
  stateCode?: string | null;
}

const PersonaLocationMap: FC<PersonaLocationMapProps> = ({
  countryCode,
  stateCode,
}) => {
  if (!stateCode) return <WorldFallbackMap countryCode={countryCode} />;
  return <StateMap countryCode={countryCode} stateCode={stateCode} />;
};

const StateMap: FC<{ countryCode: CountryValue; stateCode: string }> = ({
  countryCode,
  stateCode,
}) => {
  const { geographics } = API.Metadata.useGetGeographics();
  const [pinCoords, setPinCoords] = useState<[number, number] | null>(null);
  const [geoLoaded, setGeoLoaded] = useState(false);

  const mapConfig = useMemo(() => {
    const entry = geographics.find((g) => g.code === countryCode);
    return entry?.map ?? null;
  }, [geographics, countryCode]);

  const parseGeo = useCallback(
    (data: GeoFeature[]) => {
      if (!mapConfig?.filter_property || !mapConfig.filter_value) return data;
      return data.filter(
        (g) =>
          resolveProperty(g, mapConfig.filter_property!) ===
          mapConfig.filter_value
      );
    },
    [mapConfig]
  );

  if (!mapConfig) return <PersonaLocationMapSkeleton />;

  return (
    <Box
      style={{ aspectRatio: MAP_ASPECT, position: "relative", width: "100%" }}
    >
      {!geoLoaded && (
        <Box style={{ position: "absolute", inset: 0, zIndex: 1 }}>
          <PersonaLocationMapSkeleton />
        </Box>
      )}
      <Card
        className={`absolute inset-0 p-0 rounded-2xl overflow-hidden ${geoLoaded ? "border border-outline-200" : ""}`}
      >
        <ComposableMap
          height={MAP_HEIGHT}
          projection={mapConfig.projection}
          projectionConfig={{
            center: mapConfig.center,
            scale: mapConfig.scale,
          }}
          style={{ display: "block", height: "100%", width: "100%" }}
        >
          {/* @ts-expect-error react-simple-maps Feature type mismatch */}
          <Geographies geography={mapConfig.url} parseGeographies={parseGeo}>
            {({ geographies }: { geographies: GeoFeature[] }) => {
              if (!geoLoaded) setTimeout(() => setGeoLoaded(true), 0);
              const highlighted = geographies.find(
                (g) => resolveProperty(g, mapConfig.id_property) === stateCode
              );

              const centroid = highlighted
                ? (geoCentroid(highlighted as unknown as GeoJSON.Feature) as [
                    number,
                    number,
                  ])
                : null;

              if (
                centroid &&
                (!pinCoords ||
                  centroid[0] !== pinCoords[0] ||
                  centroid[1] !== pinCoords[1])
              ) {
                setTimeout(() => setPinCoords(centroid), 0);
              }

              return geographies
                .map((geo) => {
                  const featureId = resolveProperty(geo, mapConfig.id_property);
                  if (!featureId) return null;
                  const isHighlighted = featureId === stateCode;
                  return (
                    <Geography
                      geography={geo}
                      key={geo.rsmKey}
                      style={{
                        default: geoStyle(isHighlighted),
                        hover: geoStyle(isHighlighted),
                        pressed: geoStyle(isHighlighted),
                      }}
                    />
                  );
                })
                .filter(Boolean);
            }}
          </Geographies>

          {pinCoords && (
            <Marker coordinates={pinCoords}>
              <MapPin />
            </Marker>
          )}
        </ComposableMap>
      </Card>
    </Box>
  );
};

export const PersonaLocationMapSkeleton: FC = () => (
  <Skeleton
    className="w-full rounded-2xl"
    style={{ aspectRatio: MAP_ASPECT }}
  />
);

export default PersonaLocationMap;
